// record_gas_test (AGT, CORE-036/041/044; versionless). Every reading carries the
// tester's own "within the referenced criteria?" answer: no approved limits exist
// (DEC-CORE-005) and the app never judges a reading itself.
import { useId, useState } from 'react'
import { ErrorBanner, RuleRefs } from '@/components/evidence'
import { Banner, Button, Field, Select, Spinner, TextArea, TextInput } from '@/components/ui'
import { GAS_SPECIES, GAS_TEST_KINDS } from '@/domain/catalogue'
import type { ActionPanelProps } from '@/features/contracts'
import { fmtDate, fromLocalInput, hoursText, inputTimeArg, retestHours, toLocalInput } from '@/lib/format'
import { usePolicy, useRegistry } from '@/lib/queries'
import { PanelFrame } from '../frame'
import { usePanelClock, usePanelCommand } from '../hooks'
import {
  allowedUnits,
  buildMeasurements,
  cycleForAction,
  gasPlanOf,
  initialReadingRows,
  isStoppedCycle,
  msToIso,
  policyParameter,
  readingProblems,
  type ReadingRow,
  revisionOf,
  speciesLabel,
  textOf,
  usableText,
  withDefaultUnits,
} from '../logic'
import { CycleHeader, DateTimeField, MissingCycle, PolicyNote, SigningStatement, TextField, YesNo } from '../parts'

function ReadingFields({ row, allowed, required, onChange, onRemove }: {
  row: ReadingRow
  allowed: string[]
  required: boolean
  onChange: (patch: Partial<ReadingRow>) => void
  onRemove: () => void
}) {
  const valueId = useId()
  const unitId = useId()
  return (
    <fieldset className="rounded-lg border border-line bg-white p-3">
      <legend className="px-1 text-sm font-semibold text-ink">
        {speciesLabel(row.species_code)}
        {required ? <span className="font-normal text-ink-soft"> - required by the gas plan</span> : null}
      </legend>
      <div className="grid grid-cols-2 gap-x-3">
        <Field label="Reading" required htmlFor={valueId}>
          <TextInput
            id={valueId}
            inputMode="decimal"
            autoComplete="off"
            value={row.value}
            placeholder="Number"
            onChange={(e) => onChange({ value: e.target.value })}
          />
        </Field>
        <Field label="Unit" required htmlFor={unitId} hint={allowed.length === 0 ? 'No unit is allowed for this gas in the rules.' : undefined}>
          <Select
            id={unitId}
            value={row.unit_code}
            onChange={(e) => onChange({ unit_code: e.target.value })}
            placeholder={allowed.length === 1 ? undefined : 'Choose'}
            options={allowed.map((u) => ({ value: u, label: u }))}
          />
        </Field>
      </div>
      <YesNo
        legend="Within the referenced criteria?"
        hint="Your own judgement against the referenced method. There are no approved limits to compare with."
        name={`within-${row.species_code}`}
        value={row.within}
        onChange={(v) => onChange({ within: v })}
      />
      {!required ? (
        <Button type="button" variant="ghost" className="mt-2" onClick={onRemove}>
          Remove this gas
        </Button>
      ) : null}
    </fieldset>
  )
}

export function GasTestPanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const cycle = cycleForAction(bundle, action)
  const now = usePanelClock(bundle)
  const policy = usePolicy(bundle.policy.version_id)
  const registry = useRegistry()
  const run = usePanelCommand('record_gas_test', bundle, onDone)
  const revision = revisionOf(bundle, cycle)
  const gasPlan = gasPlanOf(revision)
  const required = gasPlan?.species ?? []
  const unitsParam = policyParameter(policy.data, 'gas.species_units')
  const units = allowedUnits(unitsParam)
  const methodParam = policyParameter(policy.data, 'gas.reference_method')
  const method = usableText(methodParam)
  const prearranged = cycle?.validation?.prearrangement ?? null

  const [instrumentId, setInstrumentId] = useState('')
  const [testKind, setTestKind] = useState('')
  const [location, setLocation] = useState(() => textOf(prearranged?.sample_location))
  // The default is the exact time the panel opened, sent with its seconds while the input
  // still shows that minute (inputTimeArg): the server compares to the second, and a
  // validated period can start mid-minute.
  const [initialObservedAt] = useState(() => msToIso(now()))
  const [observedAt, setObservedAt] = useState(() => toLocalInput(initialObservedAt))
  const [rows, setRows] = useState<ReadingRow[]>(() => initialReadingRows(required, null))
  const [notes, setNotes] = useState('')
  const [tried, setTried] = useState(false)
  const instrumentFieldId = useId()
  const kindId = useId()
  const addId = useId()
  const notesId = useId()

  const effectiveRows = withDefaultUnits(rows, units)
  const speciesChoices = units ? Object.keys(units) : GAS_SPECIES.map((g) => g.code)
  const addable = speciesChoices.filter((c) => !rows.some((r) => r.species_code === c))
  const instruments = (registry.data?.gas_instruments ?? []).filter((i) => i.site_id === bundle.permit.site.id || i.site_id === null)
  // Only a value still shown on screen is sent (the list can refresh while the form is open).
  const instrumentChosen = instruments.some((i) => i.id === instrumentId) ? instrumentId : ''
  const outside = effectiveRows.filter((r) => r.within === false)

  function updateRow(index: number, patch: Partial<ReadingRow>) {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)))
  }

  const problems: string[] = []
  if (policy.data && !units) problems.push('The allowed gas units are not set in the rules, so a test cannot be recorded.')
  if (!instrumentChosen) problems.push('Choose the instrument.')
  if (!testKind) problems.push('Choose what kind of test this is.')
  if (!location.trim()) problems.push('Enter the sample location.')
  if (!fromLocalInput(observedAt)) problems.push('Enter when you took the readings.')
  problems.push(...readingProblems(effectiveRows))

  function submit() {
    setTried(true)
    if (!cycle || problems.length > 0) return
    void run.submit({
      permit_id: bundle.permit.id,
      cycle_id: cycle.id,
      instrument_id: instrumentChosen,
      test_kind: testKind,
      sample_location: location.trim(),
      reported_observed_at: inputTimeArg(observedAt, initialObservedAt) ?? '',
      measurements: buildMeasurements(effectiveRows),
      notes: notes.trim() || null,
    })
  }

  return (
    <PanelFrame
      action={action}
      run={run}
      problems={problems}
      tried={tried}
      onCancel={onCancel}
      onSubmit={submit}
      submitLabel={outside.length > 0 ? 'Sign unsafe result (stops work)' : 'Sign gas test'}
      danger={outside.length > 0}
      blockedNote={cycle ? null : 'Cycle not found.'}
    >
      {cycle ? <CycleHeader cycle={cycle} /> : <MissingCycle />}

      {cycle && isStoppedCycle(cycle.cycle_state) ? (
        <Banner tone="warn" title="Work on this cycle is stopped" className="mb-4">
          This test is recorded only. It does not restart work: the stop stays in force until the Responsible Supervisor validates a new
          cycle. An unsafe reading is still recorded and stays on the record.
        </Banner>
      ) : null}

      <Banner tone="synthetic" title="No approved gas limits" className="mb-4">
        {bundle.permit.is_synthetic
          ? 'No approved gas limits exist yet (DEC-CORE-005). In this synthetic demo the tester declares each reading against a placeholder method.'
          : 'No approved gas limits exist yet (DEC-CORE-005). The result is classified only on the basis the rules allow.'}
        <span className="mt-1 block text-xs text-ink-soft">
          Method reference: {method ?? 'not set'} <PolicyNote param={methodParam} code="gas.reference_method" />
        </span>
      </Banner>

      {policy.isLoading ? (
        <p className="mb-4 flex items-center gap-2 text-sm text-ink-soft">
          <Spinner small /> Loading the gas rules...
        </p>
      ) : null}
      {policy.error ? (
        <div className="mb-4">
          <ErrorBanner error={policy.error} title="Could not load the gas rules" />
        </div>
      ) : null}
      {policy.data && !units ? (
        <Banner tone="bad" title="Allowed gas units are not set" className="mb-4">
          The rule value for gas units is unresolved, so a gas test cannot be recorded. <PolicyNote param={unitsParam} code="gas.species_units" />
        </Banner>
      ) : null}

      {gasPlan ? (
        <p className="mb-4 text-sm text-ink">
          Gas plan: test for {required.map(speciesLabel).join(', ') || 'no gases listed'}
          {retestHours(gasPlan) !== null ? `; retest every ${hoursText(retestHours(gasPlan) ?? 0)}` : ''}.
          <RuleRefs ruleIds={['CORE-038']} />
        </p>
      ) : (
        <p className="mb-4 text-sm text-ink-soft">The permit has no gas testing requirement. Add the gases you tested below.</p>
      )}

      {registry.isLoading ? (
        <p className="mb-4 flex items-center gap-2 text-sm text-ink-soft">
          <Spinner small /> Loading instruments...
        </p>
      ) : registry.error ? (
        <div className="mb-4">
          <ErrorBanner error={registry.error} title="Could not load the instrument list" />
        </div>
      ) : (
        <Field
          label="Instrument"
          required
          htmlFor={instrumentFieldId}
          hint={
            instruments.length === 0
              ? 'No gas instrument is registered for this site.'
              : 'The server checks the instrument is in service with a current verification.'
          }
        >
          <Select
            id={instrumentFieldId}
            value={instrumentChosen}
            onChange={(e) => setInstrumentId(e.target.value)}
            placeholder="Choose the instrument"
            options={instruments.map((i) => ({
              value: i.id,
              label: `${i.instrument_identifier} - ${i.model} (${i.status === 'in_service' ? 'in service' : i.status.replace(/_/g, ' ')}${
                i.verification_effective_to ? `, verified to ${fmtDate(i.verification_effective_to)}` : ', no verification date'
              })`,
            }))}
          />
        </Field>
      )}

      <Field label="Kind of test" required htmlFor={kindId}>
        <Select
          id={kindId}
          value={testKind}
          onChange={(e) => setTestKind(e.target.value)}
          placeholder="Choose"
          options={GAS_TEST_KINDS.map((k) => ({ value: k.code, label: k.label }))}
        />
      </Field>
      <TextField
        label="Sample location"
        required
        value={location}
        onChange={setLocation}
        placeholder="e.g. at pipe support SYN-PS-101"
        hint={textOf(prearranged?.sample_location) ? 'Filled in from the pre-arranged test. Change it if you sampled elsewhere.' : undefined}
      />
      <DateTimeField
        label="When you took the readings"
        required
        value={observedAt}
        onChange={setObservedAt}
        hint="The server also records the time you sign, separately."
      />

      <section className="mb-4 space-y-3">
        <h3 className="display text-base font-semibold text-ink">
          Readings
          <RuleRefs ruleIds={['CORE-041', 'CORE-044']} decisionIds={['DEC-CORE-005']} />
        </h3>
        {rows.length === 0 ? <p className="text-sm text-ink-soft">No gases yet. Add each gas you tested.</p> : null}
        {rows.map((r, i) => (
          <ReadingFields
            key={r.species_code}
            row={effectiveRows[i]}
            allowed={units?.[r.species_code] ?? []}
            required={required.includes(r.species_code)}
            onChange={(patch) => updateRow(i, patch)}
            onRemove={() => setRows((prev) => prev.filter((_, j) => j !== i))}
          />
        ))}
        {addable.length > 0 ? (
          <Field label="Add another gas" htmlFor={addId}>
            <Select
              id={addId}
              value=""
              onChange={(e) => {
                const code = e.target.value
                if (code) setRows((prev) => [...prev, { species_code: code, unit_code: '', value: '', within: null }])
              }}
              placeholder="Choose a gas to add"
              options={addable.map((c) => ({ value: c, label: speciesLabel(c) }))}
            />
          </Field>
        ) : null}
      </section>

      <Field label="Notes (optional)" htmlFor={notesId}>
        <TextArea id={notesId} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>

      {outside.length > 0 ? (
        <Banner tone="bad" title="This records an UNSAFE result" className="mb-4">
          You answered No for {outside.map((r) => speciesLabel(r.species_code)).join(', ')}. When you sign, the cycle is stopped at
          once and work must stop (CORE-040). Tell the PH and the RS now. The result stays on the record.
        </Banner>
      ) : null}

      <SigningStatement role="AGT" person={bundle.viewer.person}>
        <p>I took these readings myself, with the instrument shown, at the sample location and time shown.</p>
        <p>
          Each &quot;within the referenced criteria&quot; answer is my own judgement against the referenced method
          {method ? `, ${method}` : ''}. No approved gas limits exist yet.
        </p>
        {outside.length > 0 ? (
          <p className="font-semibold text-bad">At least one reading is outside the criteria. Signing stops the work.</p>
        ) : null}
        {cycle ? <p>This test is recorded on cycle {cycle.cycle_number} only.</p> : null}
      </SigningStatement>
    </PanelFrame>
  )
}
